import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { BlockNoteSchema, defaultBlockSpecs, defaultStyleSpecs } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { FormattingToolbar, useCreateBlockNote } from '@blocknote/react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { parseNoteToBlocks, serializeBlocks } from '@utils/blocknote';
import { useAutoSave } from '@hooks/useAutoSave';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useSaveTreatmentPlanMutation } from '../store/api';

import type { TreatmentPlanData } from './PatientTreatmentPlanCard';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';

// Remove media blocks (image, video, audio, file) and code block
const {
  codeBlock: _codeBlock,
  audio: _audio,
  video: _video,
  image: _image,
  file: _file,
  ...blockSpecs
} = defaultBlockSpecs;
const { code: _code, ...styleSpecs } = defaultStyleSpecs;
const treatmentPlanSchema = BlockNoteSchema.create({ blockSpecs, styleSpecs });

export interface PatientTreatmentPlanEditModalHandle {
  loadMarkdown: (markdown: string) => Promise<void>;
}

interface PatientTreatmentPlanEditModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  treatmentPlan: TreatmentPlanData | null;
  source: string;
  onImport?: () => void;
}

export const PatientTreatmentPlanEditModal = React.forwardRef<
  PatientTreatmentPlanEditModalHandle,
  PatientTreatmentPlanEditModalProps
>(({
  open,
  onClose,
  patientId,
  treatmentPlan,
  source,
  onImport,
}, ref) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { patientContextBar: contextBarTracking } = useEventTracking();

  const [treatmentPlanId, setTreatmentPlanId] = useState<string | null>(
    treatmentPlan?.treatment_plan_id || null,
  );
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const pendingMarkdownRef = useRef<string | null>(null);
  const prevOpenRef = useRef(false);
  const isLoadingContent = useRef(false);
  const triggerAutoSaveRef = useRef<((md: string) => void) | null>(null);

  const editor = useCreateBlockNote({ schema: treatmentPlanSchema });
  const [saveMutation] = useSaveTreatmentPlanMutation();

  // Expose loadMarkdown so parent can push imported content into the editor
  useImperativeHandle(ref, () => ({
    loadMarkdown: async (md: string) => {
      const blocks = await editor.tryParseMarkdownToBlocks(md);
      editor.replaceBlocks(editor.document, blocks);
      const serialized = serializeBlocks(editor.document);
      setHasLocalChanges(true);
      pendingMarkdownRef.current = serialized;
      triggerAutoSaveRef.current?.(serialized);
    },
  }), [editor]);

  // Only reset editor state when modal transitions from closed -> open
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      const loadContent = async () => {
        isLoadingContent.current = true;
        const stored = treatmentPlan?.markdown || '';
        const blocks = await parseNoteToBlocks(editor, stored);
        if (blocks.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          editor.replaceBlocks(editor.document, blocks);
        }
        setTreatmentPlanId(treatmentPlan?.treatment_plan_id || null);
        setHasLocalChanges(false);
        pendingMarkdownRef.current = null;
        // Delay clearing the flag so the onChange from replaceBlocks is ignored
        requestAnimationFrame(() => {
          isLoadingContent.current = false;
        });
      };
      loadContent();
    }
    prevOpenRef.current = open;
  }, [open, treatmentPlan, editor]);

  const performSave = useCallback(
    async (markdownToSave: string) => {
      try {
        const result = await saveMutation({
          patientId,
          markdownContent: markdownToSave,
          treatmentPlanId,
        }).unwrap();
        // Update treatment_plan_id if this was a create
        if (result?.treatment_plan_id && result.treatment_plan_id !== treatmentPlanId) {
          setTreatmentPlanId(result.treatment_plan_id);
        }
        setHasLocalChanges(false);
        pendingMarkdownRef.current = null;
      } catch (error) {
        console.error('Failed to save treatment plan:', error);
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'treatment_plan',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [patientId, treatmentPlanId, saveMutation, contextBarTracking, source],
  );

  const { triggerAutoSave, saveImmediately, status, lastSavedAt } = useAutoSave({
    onSave: performSave,
    debounceMs: 2000,
    enabled: open && hasLocalChanges,
    flushOnUnmount: true,
  });
  triggerAutoSaveRef.current = triggerAutoSave;

  const handleEditorChange = useCallback(() => {
    if (isLoadingContent.current) return;
    const serialized = serializeBlocks(editor.document);
    setHasLocalChanges(true);
    pendingMarkdownRef.current = serialized;
    triggerAutoSave(serialized);
  }, [editor, triggerAutoSave]);

  const handleBlur = useCallback(() => {
    if (pendingMarkdownRef.current !== null) {
      saveImmediately(pendingMarkdownRef.current);
      pendingMarkdownRef.current = null;
    }
  }, [saveImmediately]);

  const handleClose = useCallback(() => {
    contextBarTracking.editModalClosed({
      patient_id: patientId,
      data_type: 'treatment_plan',
      had_changes: hasLocalChanges,
      source,
    });
    if (hasLocalChanges && pendingMarkdownRef.current !== null) {
      saveImmediately(pendingMarkdownRef.current);
      pendingMarkdownRef.current = null;
    }
    onClose();
  }, [hasLocalChanges, saveImmediately, onClose, patientId, source, contextBarTracking]);

  return (
    <Dialog
      open={open}
      onClose={(_event, _reason) => {
        if (status === 'saving' || hasLocalChanges) return;
        handleClose();
      }}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '80vh',
          maxHeight: fullScreen ? '100%' : '80vh',
          bgcolor: 'background.paper',
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Treatment Plan
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {onImport && (
              <Button
                variant="outlined"
                size="small"
                onClick={onImport}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                }}
              >
                Import
              </Button>
            )}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              disabled={status === 'saving'}
              sx={{
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box
          onBlur={handleBlur}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            '& .bn-container': {
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column-reverse',
              height: '100%',
              overflow: 'auto !important',
            },
            '& .bn-editor': {
              flex: 1,
              padding: '16px',
              overflow: 'visible !important',
            },
          }}
        >
          <BlockNoteView
            editor={editor}
            editable
            theme="light"
            onChange={handleEditorChange}
            formattingToolbar={false}
            sideMenu={false}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                '& .bn-formatting-toolbar': {
                  border: 'none',
                  borderRadius: 0,
                  boxShadow: 'none',
                  padding: '4px 16px',
                  minHeight: 36,
                },
              }}
            >
              <FormattingToolbar />
            </Box>
          </BlockNoteView>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <AutosaveStatus
          status={status}
          isDirty={hasLocalChanges}
          lastSavedAt={lastSavedAt}
          verbose
        />
        <Button
          onClick={handleClose}
          variant="contained"
          disabled={status === 'saving' || hasLocalChanges}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
});

PatientTreatmentPlanEditModal.displayName = 'PatientTreatmentPlanEditModal';
